import { inject, Injectable } from '@angular/core';

import { WindowService } from 'src/shared/providers/window.service';
import { PosthogService } from 'src/shared/services/posthog.service';

import { FEATURE_FLAGS, FEATURE_FLAG_DEFAULTS } from 'src/shared/constants/feature-flags';

import { environment } from 'src/environments/environment';

/**
 * PostHog "pm-schedule-call-selection" experiment: on the Schedule Setup Call step, variant
 * `auto-learn` hides the "what do you want to do on your call?" goal radios and loads the
 * Learn-about-TIDY Calendly directly; `control` keeps the radios.
 *
 * Exposure ($feature_flag_called) is recorded at demo login (recordExposure(), from
 * DemoAccountService.loginToDemoAccount). Funnel: viewed_schedule_call_page → scheduled_call
 * (the scheduled_call KPI event is shared with the account-manager experiment). Mirrors
 * PmDemoAccountManagerService: anonymous per-device bucketing, sticky cache, no-exposure read;
 * prod vs sandbox by getFlagKey(); flags carry NO targeting.
 */
@Injectable({ providedIn: 'root' })
export class PmScheduleCallSelectionService {

  private posthogService = inject(PosthogService);
  private windowService = inject(WindowService);

  private getFlagKey(): string {
    return environment.environment === 'production'
      ? FEATURE_FLAGS.PM_SCHEDULE_CALL_SELECTION
      : FEATURE_FLAGS.PM_SCHEDULE_CALL_SELECTION_SANDBOX;
  }

  /** The currently bucketed variant (from sticky cache), for tagging funnel events / branching UI. */
  getVariant(): string {
    const flagKey = this.getFlagKey();
    return localStorage.getItem(`posthog_variant_${flagKey}`)
      || (FEATURE_FLAG_DEFAULTS[flagKey] as string);
  }

  /** Warm the variant cache early (no exposure) so the schedule-call step can branch instantly. */
  prefetchVariant(): void {
    this.resolveVariant().catch(() => { /* warm-up only */ });
  }

  /** Record experiment exposure ($feature_flag_called) — call at demo login. */
  async recordExposure(): Promise<void> {
    const variant = await this.resolveVariant();
    this.posthogService.capture('$feature_flag_called', {
      $feature_flag: this.getFlagKey(),
      $feature_flag_response: variant,
    });
  }

  async resolveVariant(): Promise<string> {
    const flagKey = this.getFlagKey();
    const cacheKey = `posthog_variant_${flagKey}`;

    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      return cached;
    }

    await this.posthogService.waitForIdentification();
    await this.posthogService.waitForFlags();
    const raw = await this.posthogService.getFeatureFlagNoExposure(flagKey);
    const variant = (raw as string) || (FEATURE_FLAG_DEFAULTS[flagKey] as string);

    if (raw !== null && raw !== undefined && raw !== false) {
      localStorage.setItem(cacheKey, variant);
    }

    return variant;
  }

  /** Capture a funnel event tagged with the resolved variant and the platform form factor. */
  capture(event: string, properties: Record<string, any> = {}): void {
    this.posthogService.capture(event, {
      variant: this.getVariant(),
      is_desktop: this.windowService.isDesktopRes,
      ...properties,
    });
  }
}
